import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CalendarCheck, ChevronLeft, ChevronRight, Minus, Plus, RefreshCw } from 'lucide-react';
import { RESERVATION_TRANSITIONS, canTransition } from '@/lib/domain';
import { ReservationStatus, TableState } from '@/lib/shared-types';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { Badge, toneForStatus } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Icon } from '@/components/ui/icon';
import { Input } from '@/components/ui/input';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { TableWrap, Table, THead, TH, TR, TD, TableEmpty } from '@/components/ui/table';
import { SummaryRow } from '@/components/ui/detail';
import { Tabs } from '@/components/ui/tabs';
import { useRoutedTabs } from '@/Pages/routedTabs';
import { RESERVATION_TABS } from '@/Pages/tabRoutes';
import HostQueuePanel from './components/HostQueuePanel';
import {
  dayOf,
  defaultEndsAt,
  reservationBody,
  shiftWeek,
  slotIso,
  timeOf,
  timeSlots,
  weekStrip,
} from './reservationForm';
import {
  apiMessage,
  endOfDayIso,
  formatDateTime,
  formatTime,
  startOfDayIso,
  toDayInput,
  useBranches,
  useQueryParams,
} from './shared';

const RESERVATION_STATUSES = Object.values(ReservationStatus);

/** The service window the chip grid offers. Not availability — see the form. */
const SLOTS = timeSlots('18:00', '22:30', 30);

/**
 * `SEATABLE_TABLE_STATES` in `reservations.service.ts` — seating takes the table
 * over, so it only accepts a table that is free, held for this very booking, or
 * being wiped down. Anything else (OCCUPIED, PAYMENT_PENDING, BLOCKED,
 * OUT_OF_SERVICE) is a 409 the moment Seat is pressed.
 */
const SEATABLE_TABLE_STATES: string[] = [
  TableState.AVAILABLE,
  TableState.RESERVED,
  TableState.CLEANING,
];

interface VenueTableRow {
  id: string;
  number: string;
  name: string | null;
  seats: number;
  state: TableState;
  /** `GET /tables` includes it, which is what lets the picker group by room. */
  area?: { id: string; name: string } | null;
}

interface ResourceRow {
  id: string;
  name: string;
  type: string;
}

interface Reservation {
  id: string;
  branchId: string;
  status: ReservationStatus;
  customerName: string | null;
  customerPhone: string | null;
  partySize: number;
  startsAt: string;
  endsAt: string | null;
  notes: string | null;
  table: { id: string; number: string; seats: number } | null;
  resource: { id: string; name: string; type: string } | null;
  customer: { id: string; name: string; phone: string | null } | null;
}

interface Availability {
  available: boolean;
  startsAt: string;
  endsAt: string;
  conflicts: { id: string; status: string; startsAt: string; endsAt: string; partySize: number }[];
}

/**
 * What `POST /reservations/:id/seat` made.
 *
 * A party and a host participant used to come back with it, because charges
 * routed to the party. They do not exist any more: a table booking becomes a
 * dine-in `Session` and that session is the whole of what the booking became,
 * while a resource booking starts a `ResourceSession` attached to the booked
 * table. The customer is written back onto the reservation instead, which is
 * where the CRM already looks for it.
 */
interface SeatResult {
  reservation: { id: string; status: string };
  session: { id: string; tableId: string | null } | null;
  resourceSession: { id: string; resourceId: string; status: string } | null;
}

/**
 * P12-02 / P12-04 — the reservation book, and §13 / ADR-0018's host queue.
 *
 * Two tabs rather than a new nav row: the queue is a second view of
 * overlapping data — bookings due soon from this same calendar, and walk-ins
 * from the general waitlist — not a separate feature with its own place in
 * the rail. `RESERVATION_TABS` in `Pages/tabRoutes.ts` is the gate; this shell
 * only chooses which tab's component to mount.
 */
export default function Reservations() {
  const { t } = useTranslation();
  const routed = useRoutedTabs('/reservations', RESERVATION_TABS);
  if (!routed.open) return routed.redirect;
  const { tabs, current, select } = routed;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-semibold">{t('nav.reservations')}</h1>
      </div>
      <Tabs
        tabs={tabs.map((tab) => ({ key: tab.id, label: t(tab.labelKey) }))}
        value={current}
        onChange={select}
        label={t('nav.reservations')}
        idPrefix="reservations"
        className="scrollbar-thin gap-x-5 overflow-x-auto"
      />
      {current === 'calendar' && <ReservationsCalendar />}
      {current === 'host-queue' && <HostQueuePanel />}
    </div>
  );
}

/**
 * The existing calendar screen, unchanged apart from its name — every hook
 * and every line of markup below is exactly what `Reservations` used to be
 * before the host-queue tab was added beside it.
 *
 * A day at a time, because that is how a host works the list. Seating is the
 * interesting step: it is not a status flip but a transaction that opens either
 * a dine-in session on the booked table or a running resource session — so the
 * screen reports back exactly what was created.
 */
function ReservationsCalendar() {
  const { t, i18n } = useTranslation();
  const { can } = usePermission();
  const branches = useBranches();

  const [day, setDay] = useState(() => toDayInput(new Date()));
  const [status, setStatus] = useState('');
  const [branchId, setBranchId] = useState('');
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [subject, setSubject] = useState<'table' | 'resource'>('table');
  const [availability, setAvailability] = useState<Availability | null>(null);
  const [seatResult, setSeatResult] = useState<SeatResult | null>(null);
  const [cancelTarget, setCancelTarget] = useState<Reservation | null>(null);
  const [cancelReason, setCancelReason] = useState('');

  const [form, setForm] = useState({
    branchId: '',
    tableId: '',
    resourceId: '',
    customerName: '',
    customerPhone: '',
    partySize: '2',
    startsAt: '',
    endsAt: '',
    notes: '',
  });

  const listParams = useQueryParams({
    from: day ? startOfDayIso(day) : '',
    to: day ? endOfDayIso(day) : '',
    status,
    branchId,
    search,
    sortBy: 'startsAt',
    sortOrder: 'asc',
    limit: 200,
  });
  const list = useApi<unknown>('/reservations', listParams);
  const reservations = rowsOf<Reservation>(list.data).rows;

  /**
   * Scoped to the branch the form is booking into. `QueryTablesDto` and
   * `QueryResourcesDto` both declare `branchId`, and an unscoped picker offers
   * tables in another branch that `create` would refuse.
   */
  const subjectParams = useQueryParams({ limit: 200, branchId: form.branchId });
  const tables = useApi<unknown>(can('tables.view') ? '/tables' : null, subjectParams);
  const tableRows = rowsOf<VenueTableRow>(tables.data).rows;

  const resources = useApi<unknown>(can('resources.view') ? '/resources' : null, subjectParams);
  const resourceRows = rowsOf<ResourceRow>(resources.data).rows;

  /**
   * `GET /reservations` embeds only id/number/seats for the table, so the state
   * seating actually depends on has to come from the table list. Absence is not
   * evidence: without `tables.view`, or for a table past this page, the map has
   * no entry and Seat stays enabled — the server is still the one that decides.
   */
  const tableStateById = useMemo(
    () => new Map(tableRows.map((row) => [row.id, row.state])),
    [tableRows],
  );

  const seatBlockedState = (r: Reservation): TableState | null => {
    if (!r.table) return null;
    const state = tableStateById.get(r.table.id);
    if (!state || SEATABLE_TABLE_STATES.includes(state)) return null;
    return state;
  };

  // --------------------------------------------------------------- actions

  const run = async (
    key: string,
    request: () => Promise<unknown>,
    successKey: string,
  ): Promise<boolean> => {
    setBusy(key);
    try {
      await request();
      toast.success(t(successKey));
      list.reload();
      return true;
    } catch (e) {
      toast.error(apiMessage(e, t('operations.errors.actionFailed')));
      return false;
    } finally {
      setBusy(null);
    }
  };

  /**
   * The slot answer is only about the subject and window it was asked for, so
   * changing either has to retract it — otherwise a stale "the slot is free"
   * would keep Save enabled into a `409` from `assertSlotFree()`.
   */
  const patchSlot = (patch: Partial<typeof form>) => {
    setAvailability(null);
    setForm((f) => ({ ...f, ...patch }));
  };

  const subjectId = subject === 'table' ? form.tableId : form.resourceId;
  const slotComplete = !!form.branchId.trim() && !!subjectId && !!form.startsAt;

  /**
   * `form.startsAt` stays the one value the DTO is built from; the day strip
   * and the time chips are two views of it. Splitting it into two pieces of
   * state would let them disagree, and the disagreement would be invisible —
   * the request only ever carries one of them.
   */
  const slotDay = dayOf(form.startsAt) || toDayInput(new Date());
  const slotTime = timeOf(form.startsAt);
  const strip = useMemo(() => weekStrip(slotDay), [slotDay]);
  const setSlot = (nextDay: string, nextTime: string) =>
    patchSlot({ startsAt: slotIso(nextDay, nextTime || SLOTS[Math.floor(SLOTS.length / 2)]) });

  /** Tables in the rooms they stand in, which is how a host picks one. */
  const tablesByArea = useMemo(() => {
    const groups = new Map<string, VenueTableRow[]>();
    for (const table of tableRows) {
      const key = table.area?.name ?? t('operations.reservations.noArea');
      groups.set(key, [...(groups.get(key) ?? []), table]);
    }
    return [...groups.entries()];
  }, [tableRows, t]);

  const subjectLabel =
    subject === 'table'
      ? (() => {
          const table = tableRows.find((row) => row.id === form.tableId);
          if (!table) return form.tableId || null;
          const area = table.area?.name;
          const name = table.name ?? t('operations.reservations.tableRef', { number: table.number });
          return area ? `${name} (${area})` : name;
        })()
      : (resourceRows.find((row) => row.id === form.resourceId)?.name ?? (form.resourceId || null));

  const checkAvailability = async () => {
    if (!subjectId || !form.startsAt) {
      toast.error(t('operations.reservations.availabilityNeedsSlot'));
      return;
    }
    setBusy('availability');
    try {
      const { data } = await api.get<Availability>('/reservations/availability', {
        params: {
          ...(subject === 'table' ? { tableId: subjectId } : { resourceId: subjectId }),
          startsAt: new Date(form.startsAt).toISOString(),
          ...(form.endsAt ? { endsAt: new Date(form.endsAt).toISOString() } : {}),
        },
      });
      setAvailability(data);
      toast[data.available ? 'success' : 'warning'](
        t(data.available ? 'operations.reservations.slotFree' : 'operations.reservations.slotBusy'),
      );
    } catch (e) {
      toast.error(apiMessage(e, t('operations.errors.actionFailed')));
    } finally {
      setBusy(null);
    }
  };

  const createReservation = async () => {
    if (!slotComplete) {
      toast.error(t('operations.reservations.createIncomplete'));
      return;
    }
    setBusy('create');
    try {
      // Built from `RESERVATION_DTO_FIELDS`. Under `forbidNonWhitelisted` one
      // undeclared property — the frame's `email`, for instance — rejects the
      // whole booking rather than being dropped.
      await api.post('/reservations', reservationBody(form, subject));
      toast.success(t('operations.reservations.created'));
      setCreateOpen(false);
      setAvailability(null);
      setForm({
        branchId: '',
        tableId: '',
        resourceId: '',
        customerName: '',
        customerPhone: '',
        partySize: '2',
        startsAt: '',
        endsAt: '',
        notes: '',
      });
      list.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('operations.errors.actionFailed')));
    } finally {
      setBusy(null);
    }
  };

  const seat = async (reservation: Reservation) => {
    setBusy(`seat:${reservation.id}`);
    try {
      const { data } = await api.post<SeatResult>(`/reservations/${reservation.id}/seat`, {});
      setSeatResult(data);
      toast.success(t('operations.reservations.seated'));
      list.reload();
    } catch (e) {
      toast.error(apiMessage(e, t('operations.errors.actionFailed')));
    } finally {
      setBusy(null);
    }
  };

  const submitCancel = async () => {
    if (!cancelTarget) return;
    const done = await run(
      `cancel:${cancelTarget.id}`,
      () =>
        api.post(`/reservations/${cancelTarget.id}/cancel`, {
          reason: cancelReason.trim() || undefined,
        }),
      'operations.reservations.cancelled',
    );
    if (done) {
      setCancelTarget(null);
      setCancelReason('');
    }
  };

  const shiftDay = (days: number) => {
    const base = day ? new Date(`${day}T12:00:00`) : new Date();
    base.setDate(base.getDate() + days);
    setDay(toDayInput(base));
  };

  // ---------------------------------------------------------------- render

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-semibold">{t('nav.reservations')}</h1>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => list.reload()} disabled={list.loading}>
            <RefreshCw className="h-4 w-4" />
            {t('operations.common.refresh')}
          </Button>
          {can('reservations.create') && (
            <Button
              onClick={() => {
                setForm((f) => ({
                  ...f,
                  branchId: f.branchId || branches[0]?.id || '',
                  startsAt: f.startsAt || `${day}T19:00`,
                }));
                setCreateOpen(true);
              }}
            >
              <Plus className="h-4 w-4" />
              {t('operations.reservations.create')}
            </Button>
          )}
        </div>
      </div>

      <Card>
        <CardContent className="grid gap-3 p-4 [&>*]:min-w-0 sm:grid-cols-2 lg:grid-cols-5">
          <Field label={t('operations.reservations.day')}>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => shiftDay(-1)}>
                {t('common.back')}
              </Button>
              <Input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
              <Button variant="outline" size="sm" onClick={() => shiftDay(1)}>
                {t('common.next')}
              </Button>
            </div>
          </Field>

          <Field label={t('operations.filters.status')}>
            <Select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">{t('operations.filters.any')}</option>
              {RESERVATION_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`operations.reservationStatus.${s}`)}
                </option>
              ))}
            </Select>
          </Field>

          {branches.length > 0 && (
            <Field label={t('operations.filters.branch')}>
              <Select value={branchId} onChange={(e) => setBranchId(e.target.value)}>
                <option value="">{t('operations.filters.allBranches')}</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}

          <Field label={t('operations.filters.search')}>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('operations.reservations.searchHint')}
            />
          </Field>
        </CardContent>
      </Card>

      {list.error ? (
        <ErrorState message={list.error} onRetry={list.reload} />
      ) : list.loading && !reservations.length ? (
        <LoadingState label={t('common.loading')} />
      ) : !reservations.length ? (
        <EmptyState
          title={t('operations.reservations.empty')}
          hint={t('operations.reservations.emptyHint')}
        />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('operations.reservations.slot')}</TH>
                <TH>{t('operations.reservations.guest')}</TH>
                <TH>{t('operations.reservations.subject')}</TH>
                <TH>{t('operations.reservations.partySize')}</TH>
                <TH>{t('operations.filters.status')}</TH>
                <TH className="text-end">{t('operations.common.actions')}</TH>
              </TR>
            </THead>
            <tbody>
              {!reservations.length ? (
                <TableEmpty colSpan={6}>{t('operations.reservations.empty')}</TableEmpty>
              ) : (
                reservations.map((r) => (
                  <TR key={r.id}>
                    <TD className="whitespace-nowrap">
                      <div className="font-medium">{formatTime(r.startsAt, i18n.language)}</div>
                      <div className="text-xs text-ink-muted">
                        {r.endsAt ? formatTime(r.endsAt, i18n.language) : '—'}
                      </div>
                    </TD>
                    <TD>
                      <div>{r.customerName ?? r.customer?.name ?? '—'}</div>
                      {(r.customerPhone ?? r.customer?.phone) && (
                        <div className="text-xs text-ink-muted" dir="ltr">
                          {r.customerPhone ?? r.customer?.phone}
                        </div>
                      )}
                      {r.notes && <div className="text-xs text-amber-700 dark:text-amber-400">{r.notes}</div>}
                    </TD>
                    <TD>
                      {r.table
                        ? t('operations.reservations.tableRef', { number: r.table.number })
                        : (r.resource?.name ?? '—')}
                    </TD>
                    <TD>{r.partySize}</TD>
                    <TD>
                      <Badge tone={toneForStatus(r.status)}>
                        {t(`operations.reservationStatus.${r.status}`)}
                      </Badge>
                    </TD>
                    <TD>
                      <div className="flex flex-wrap justify-end gap-1.5">
                        {can('reservations.update') &&
                          canTransition(
                            RESERVATION_TRANSITIONS,
                            r.status,
                            ReservationStatus.CONFIRMED,
                          ) && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busy === `confirm:${r.id}`}
                              onClick={() =>
                                run(
                                  `confirm:${r.id}`,
                                  () => api.post(`/reservations/${r.id}/confirm`, {}),
                                  'operations.reservations.confirmed',
                                )
                              }
                            >
                              {t('common.confirm')}
                            </Button>
                          )}

                        {can('reservations.update') &&
                          canTransition(
                            RESERVATION_TRANSITIONS,
                            r.status,
                            ReservationStatus.SEATED,
                          ) && (
                            <Button
                              size="sm"
                              disabled={busy === `seat:${r.id}` || !!seatBlockedState(r)}
                              title={
                                seatBlockedState(r)
                                  ? t('operations.reservations.tableNotSeatable', {
                                      number: r.table?.number ?? '',
                                      state: t(`table.state.${seatBlockedState(r) as string}`),
                                    })
                                  : undefined
                              }
                              onClick={() => seat(r)}
                            >
                              <CalendarCheck className="h-4 w-4" />
                              {t('operations.reservations.seat')}
                            </Button>
                          )}

                        {can('reservations.update') &&
                          canTransition(
                            RESERVATION_TRANSITIONS,
                            r.status,
                            ReservationStatus.NO_SHOW,
                          ) && (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={busy === `noshow:${r.id}`}
                              onClick={() =>
                                run(
                                  `noshow:${r.id}`,
                                  () => api.post(`/reservations/${r.id}/no-show`, {}),
                                  'operations.reservations.noShowRecorded',
                                )
                              }
                            >
                              {t('operations.reservations.noShow')}
                            </Button>
                          )}

                        {can('reservations.cancel') &&
                          canTransition(
                            RESERVATION_TRANSITIONS,
                            r.status,
                            ReservationStatus.CANCELLED,
                          ) && (
                            <Button size="sm" variant="danger" onClick={() => setCancelTarget(r)}>
                              {t('common.cancel')}
                            </Button>
                          )}
                      </div>
                    </TD>
                  </TR>
                ))
              )}
            </tbody>
          </Table>
        </TableWrap>
      )}

      {/* ------------------------------------------------------ create slot */}
      <Dialog
        open={createOpen}
        onClose={() => {
          setCreateOpen(false);
          setAvailability(null);
        }}
        title={t('operations.reservations.create')}
        className="max-w-5xl"
        footer={
          <>
            <Button
              variant="ghost"
              onClick={checkAvailability}
              disabled={busy === 'availability' || !subjectId || !form.startsAt}
              title={
                !subjectId || !form.startsAt
                  ? t('operations.reservations.availabilityNeedsSlot')
                  : undefined
              }
            >
              {t('operations.reservations.checkAvailability')}
            </Button>
            {/*
              `create` runs `assertSlotFree()`, so saving into a window the
              availability check has just reported busy is a guaranteed 409.
              Only a check for THIS slot counts — `patchSlot` drops the answer
              the moment the subject or the window moves.
            */}
            <Button
              onClick={createReservation}
              disabled={busy === 'create' || !slotComplete || availability?.available === false}
              title={
                !slotComplete
                  ? t('operations.reservations.createIncomplete')
                  : availability?.available === false
                    ? t('operations.reservations.slotBusy')
                    : undefined
              }
            >
              {t('operations.reservations.create')}
            </Button>
          </>
        }
      >
        <div className="grid gap-6 [&>*]:min-w-0 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:items-start">
          <div className="space-y-4">
            {/* Not on the frame, and `CreateReservationDto.branchId` is
                @IsNotEmpty — without it every Create is a 400. */}
            <Field label={t('operations.filters.branch')} required>
              {branches.length ? (
                <Select
                  value={form.branchId}
                  onChange={(e) => patchSlot({ branchId: e.target.value, tableId: '', resourceId: '' })}
                >
                  <option value="">{t('operations.filters.choose')}</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </Select>
              ) : (
                <Input
                  value={form.branchId}
                  onChange={(e) => patchSlot({ branchId: e.target.value })}
                  placeholder={t('operations.reservations.branchIdHint')}
                />
              )}
            </Field>

            <div className="grid gap-4 [&>*]:min-w-0 sm:grid-cols-2">
              <Field label={t('operations.reservations.guest')}>
                <Input
                  value={form.customerName}
                  onChange={(e) => setForm({ ...form, customerName: e.target.value })}
                  placeholder={t('operations.reservations.guestHint')}
                />
              </Field>
              {/* No Email field. `Reservation` has no email column and
                  `CreateReservationDto` does not declare one, so under
                  `forbidNonWhitelisted` sending it would 400 the whole booking
                  — and the route matches or creates the customer by phone. */}
              <Field label={t('operations.reservations.phone')}>
                <Input
                  dir="ltr"
                  value={form.customerPhone}
                  onChange={(e) => setForm({ ...form, customerPhone: e.target.value })}
                  placeholder="+9665…"
                />
              </Field>
              <Field label={t('operations.reservations.partySize')} required>
                <QuantityStepper
                  value={form.partySize}
                  min={1}
                  max={200}
                  onChange={(partySize) => setForm({ ...form, partySize })}
                  decreaseLabel={t('operations.reservations.fewerGuests')}
                  increaseLabel={t('operations.reservations.moreGuests')}
                />
              </Field>
            </div>

            <div className="space-y-3 border-t pt-4">
              <Field label={t('operations.reservations.day')} required>
                <div className="rounded-lg border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-medium">
                      {new Intl.DateTimeFormat(i18n.language, {
                        month: 'long',
                        year: 'numeric',
                      }).format(new Date(`${strip[0]}T12:00:00`))}
                    </span>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => setSlot(shiftWeek(slotDay, -1), slotTime)}
                        aria-label={t('common.back')}
                        className="rounded p-1 hover:bg-hover"
                      >
                        <Icon as={ChevronLeft} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setSlot(shiftWeek(slotDay, 1), slotTime)}
                        aria-label={t('common.next')}
                        className="rounded p-1 hover:bg-hover"
                      >
                        <Icon as={ChevronRight} />
                      </button>
                    </div>
                  </div>
                  {/* Seven equal cells cannot hold an Arabic weekday at 320px
                      — `السبت` is three times the width of `Sat` — so the strip
                      scrolls inside itself rather than clipping the labels or
                      pushing the page sideways. */}
                  <div className="scrollbar-thin -mx-1 mt-2 overflow-x-auto px-1">
                    <div className="grid min-w-[19rem] grid-cols-7 gap-1">
                      {strip.map((value) => {
                        const date = new Date(`${value}T12:00:00`);
                        const picked = value === slotDay;
                        return (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setSlot(value, slotTime)}
                            aria-pressed={picked}
                            className={
                              picked
                                ? 'flex min-w-0 flex-col items-center gap-0.5 rounded-lg bg-accent-solid px-1 py-1.5 text-xs text-accent-on'
                                : 'flex min-w-0 flex-col items-center gap-0.5 rounded-lg px-1 py-1.5 text-xs hover:bg-hover'
                            }
                          >
                            <span
                              className={
                                picked
                                  ? 'max-w-full truncate opacity-80'
                                  : 'max-w-full truncate text-ink-muted'
                              }
                            >
                              {new Intl.DateTimeFormat(i18n.language, {
                                weekday: 'short',
                              }).format(date)}
                            </span>
                            <span className="font-medium tabular-nums">{date.getDate()}</span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </Field>

              {/*
                The frame's ten chips are drawn individually enabled and
                disabled. `GET /reservations/availability` answers **one**
                subject and one window (`assertExactlyOneSubject`), so a live
                grid would be ten calls per table — sixty for the six drawn.
                These are the times a host may pick; whether one is free is
                still the Check availability button's answer, which is what
                moves a clash before the write instead of after it.
              */}
              <Field label={t('operations.reservations.slot')} required>
                <div className="flex flex-wrap gap-2">
                  {SLOTS.map((value) => {
                    const picked = value === slotTime;
                    return (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setSlot(slotDay, value)}
                        aria-pressed={picked}
                        className={
                          picked
                            ? 'rounded-md bg-accent-solid px-3 py-1.5 text-xs font-semibold text-accent-on'
                            : 'rounded-md border border-line-strong px-3 py-1.5 text-xs font-semibold hover:bg-hover'
                        }
                      >
                        {formatTime(`${slotDay}T${value}`, i18n.language)}
                      </button>
                    );
                  })}
                </div>
              </Field>

              {/* Kept: `endsAt` is optional on the DTO and this is the only way
                  to book a three-hour table. Blank means the server's own
                  default, which the summary states. */}
              <Field
                label={t('operations.reservations.endsAt')}
                hint={t('operations.reservations.endsAtHint')}
              >
                <Input
                  type="datetime-local"
                  value={form.endsAt}
                  onChange={(e) => patchSlot({ endsAt: e.target.value })}
                />
              </Field>
            </div>

            <Field label={t('operations.reservations.notes')}>
              <Textarea
                rows={3}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder={t('operations.reservations.notesHint')}
              />
            </Field>
          </div>

          <div className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>
                  {t('operations.reservations.assignment')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* §20 is a table **or** a resource. Dropping the resource half,
                    as the frame does, removes the only dashboard path to book a
                    PS5 booth, a billiards table or a spa room. */}
                <div className="flex flex-wrap gap-4">
                  {(['table', 'resource'] as const).map((kind) => (
                    <label key={kind} className="flex items-center gap-2 text-sm">
                      <input
                        type="radio"
                        name="reservation-subject"
                        className="h-4 w-4"
                        checked={subject === kind}
                        onChange={() => {
                          setAvailability(null);
                          setSubject(kind);
                        }}
                      />
                      {t(`operations.reservations.subjects.${kind}`)}
                    </label>
                  ))}
                </div>

                {subject === 'table' ? (
                  tableRows.length ? (
                    <div className="space-y-3">
                      {tablesByArea.map(([area, group]) => (
                        <div key={area}>
                          <p className="mb-1.5 text-xs uppercase tracking-wide text-ink-muted rtl:tracking-normal">
                            {area}
                          </p>
                          <div className="flex flex-wrap gap-2">
                            {group.map((table) => {
                              const picked = table.id === form.tableId;
                              return (
                                <button
                                  key={table.id}
                                  type="button"
                                  onClick={() => patchSlot({ tableId: table.id, resourceId: '' })}
                                  aria-pressed={picked}
                                  title={t('table.seats', { count: table.seats })}
                                  className={
                                    picked
                                      ? 'grid h-14 w-14 place-items-center rounded-full bg-accent-solid text-sm font-semibold text-accent-on'
                                      : 'grid h-14 w-14 place-items-center rounded-full border border-line-strong text-sm font-semibold hover:bg-hover'
                                  }
                                >
                                  {table.name ?? table.number}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    // Without `tables.view` there is no list to draw; the id
                    // still books, so the fallback stays rather than the screen
                    // becoming unusable for a host who may create but not browse.
                    <Field label={t('operations.reservations.subjects.table')}>
                      <Input
                        value={form.tableId}
                        onChange={(e) => patchSlot({ tableId: e.target.value, resourceId: '' })}
                        placeholder={t('operations.reservations.tableIdHint')}
                      />
                    </Field>
                  )
                ) : resourceRows.length ? (
                  <div className="flex flex-wrap gap-2">
                    {resourceRows.map((resource) => {
                      const picked = resource.id === form.resourceId;
                      return (
                        <button
                          key={resource.id}
                          type="button"
                          onClick={() => patchSlot({ resourceId: resource.id, tableId: '' })}
                          aria-pressed={picked}
                          className={
                            picked
                              ? 'rounded-lg bg-accent-solid px-3 py-2 text-sm text-accent-on'
                              : 'rounded-lg border border-line-strong px-3 py-2 text-sm hover:bg-hover'
                          }
                        >
                          {resource.name}
                        </button>
                      );
                    })}
                  </div>
                ) : (
                  <Field label={t('operations.reservations.subjects.resource')}>
                    <Input
                      value={form.resourceId}
                      onChange={(e) => patchSlot({ resourceId: e.target.value, tableId: '' })}
                      placeholder={t('operations.reservations.resourceIdHint')}
                    />
                  </Field>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>
                  {t('operations.reservations.summary')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1.5">
                <SummaryRow label={t('operations.reservations.day')}>
                  {slotDay
                    ? new Intl.DateTimeFormat(i18n.language, {
                        weekday: 'short',
                        month: 'short',
                        day: 'numeric',
                      }).format(new Date(`${slotDay}T12:00:00`))
                    : '—'}
                </SummaryRow>
                <SummaryRow label={t('operations.reservations.slot')}>
                  {slotTime ? formatTime(`${slotDay}T${slotTime}`, i18n.language) : '—'}
                </SummaryRow>
                <SummaryRow label={t('operations.reservations.guestCount')}>
                  {form.partySize || '—'}
                </SummaryRow>
                <SummaryRow label={t('operations.reservations.subject')} tone="accent">
                  {subjectLabel ?? '—'}
                </SummaryRow>
                {/* Not "2 hours (default)". `DEFAULT_SLOT_MINUTES` is 90, and a
                    summary must not state a duration the server will not use. */}
                <SummaryRow label={t('operations.reservations.endsAt')}>
                  {form.endsAt
                    ? formatTime(form.endsAt, i18n.language)
                    : slotTime
                      ? formatTime(defaultEndsAt(form.startsAt), i18n.language)
                      : '—'}
                </SummaryRow>
              </CardContent>
            </Card>

            {availability && (
              <div
                className={
                  availability.available
                    ? 'rounded-lg border border-green-300 dark:border-green-800 bg-green-50 dark:bg-green-950/50 p-3 text-sm text-green-800 dark:text-green-300'
                    : 'rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 p-3 text-sm text-amber-800 dark:text-amber-300'
                }
              >
                <p className="font-medium">
                  {availability.available
                    ? t('operations.reservations.slotFree')
                    : t('operations.reservations.slotBusy')}
                </p>
                <p className="text-xs">
                  {formatDateTime(availability.startsAt, i18n.language)} →{' '}
                  {formatDateTime(availability.endsAt, i18n.language)}
                </p>
                {!!availability.conflicts.length && (
                  <ul className="mt-1 space-y-0.5 text-xs">
                    {availability.conflicts.map((conflict) => (
                      <li key={conflict.id}>
                        {formatTime(conflict.startsAt, i18n.language)} –{' '}
                        {formatTime(conflict.endsAt, i18n.language)} ·{' '}
                        {t(`operations.reservationStatus.${conflict.status}`)} ·{' '}
                        {t('operations.reservations.partySize')}: {conflict.partySize}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </Dialog>

      {/* ------------------------------------------------- what seating made */}
      <Dialog
        open={!!seatResult}
        onClose={() => setSeatResult(null)}
        title={t('operations.reservations.seatedTitle')}
        description={t('operations.reservations.seatedHint')}
        footer={
          <Button onClick={() => setSeatResult(null)}>{t('common.close')}</Button>
        }
      >
        <Card>
          <CardHeader>
            <CardTitle>{t('operations.reservations.created')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <div className="flex justify-between gap-3">
              <span className="text-ink-muted">{t('operations.reservations.visit')}</span>
              <span className="font-medium">
                {seatResult?.session
                  ? t('operations.reservations.tableSessionOpened')
                  : seatResult?.resourceSession
                    ? t('operations.reservations.resourceSessionStarted')
                    : '—'}
              </span>
            </div>
          </CardContent>
        </Card>
      </Dialog>

      {/* ------------------------------------------------------ cancel slot */}
      <Dialog
        open={!!cancelTarget}
        onClose={() => {
          setCancelTarget(null);
          setCancelReason('');
        }}
        title={t('operations.reservations.cancelTitle')}
        description={t('operations.reservations.cancelHint')}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setCancelTarget(null);
                setCancelReason('');
              }}
            >
              {t('common.back')}
            </Button>
            <Button variant="danger" onClick={submitCancel} disabled={!!busy}>
              {t('common.confirm')}
            </Button>
          </>
        }
      >
        <Field label={t('operations.common.reason')}>
          <Textarea
            rows={3}
            value={cancelReason}
            onChange={(e) => setCancelReason(e.target.value)}
          />
        </Field>
      </Dialog>
    </div>
  );
}

/**
 * The −/value/+ row 64:407 draws for the party size.
 *
 * Named `QuantityStepper` because "Stepper" was already taken in this repo by
 * the wizard step indicator, now `components/ui/stepper.tsx`. Same word,
 * unrelated control: that one says which of four screens you are on, this one
 * counts guests.
 *
 * A stepper rather than a bare `<input type=number>` because the value is
 * bounded on both sides by the DTO — `@Min(1) @Max(200)` — and the buttons are
 * where that bound can be stated instead of discovered at save time. The input
 * itself stays typable: a party of 40 is four taps of a keyboard and 38 of a
 * button.
 */
function QuantityStepper({
  value,
  min,
  max,
  onChange,
  decreaseLabel,
  increaseLabel,
}: {
  value: string;
  min: number;
  max: number;
  onChange: (next: string) => void;
  decreaseLabel: string;
  increaseLabel: string;
}) {
  const current = Number(value) || min;
  const step = (delta: number) =>
    onChange(String(Math.min(max, Math.max(min, current + delta))));

  return (
    <div className="flex h-11 items-center rounded-lg border border-line-strong">
      <button
        type="button"
        onClick={() => step(-1)}
        disabled={current <= min}
        aria-label={decreaseLabel}
        className="grid h-full w-11 place-items-center rounded-s-lg hover:bg-hover disabled:opacity-50"
      >
        <Icon as={Minus} />
      </button>
      <input
        value={value}
        inputMode="numeric"
        onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ''))}
        onBlur={() => onChange(String(Math.min(max, Math.max(min, Number(value) || min))))}
        className="h-full min-w-0 flex-1 bg-transparent text-center text-sm tabular-nums focus-visible:outline-none"
      />
      <button
        type="button"
        onClick={() => step(1)}
        disabled={current >= max}
        aria-label={increaseLabel}
        className="grid h-full w-11 place-items-center rounded-e-lg hover:bg-hover disabled:opacity-50"
      >
        <Icon as={Plus} />
      </button>
    </div>
  );
}
